import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import { IProduct } from '../../shared/models/sale/product';
import { ISaleMovementListItem } from '../../shared/models/sale/sale-movement-list-item';
import { ISaleMovement } from '../../shared/models/sale/sale-movement';
import { CreateSaleMovementRequest } from '../../shared/models/sale/create-sale-movement-request';
import { PaymentMethodGroupDto } from '../../shared/dtos/sale/payment-method-group.dto';
import {
  ISaleDespatch,
  ISaleDespatchSelectableProduct,
} from '../../shared/models/sale/sale-despatch';
import {
  LegacySaleMovementResponse,
  normalizeSaleMovement,
  normalizeSaleMovementPage,
  toSaleMovementApiRequest,
} from '../../shared/models/sale/sale-movement-adapters';

export type SaleOperationStatus =
  | { status: 'processing' }
  | { status: 'done'; response: ISaleMovement }
  | { status: 'failed'; error?: { message?: string } };

export const saleApi = createApi({
  reducerPath: 'saleApi',
  baseQuery,
  tagTypes: ['Coupons', 'Coupon'],
  endpoints: (builder) => ({
    productList: builder.query<IPagedResult<IProduct>, string>({
      query: (params) => ({
        url: `/api/materials?${params}`,
        method: 'GET',
      }),
    }),
    addSale: builder.mutation<
      ISaleMovement | { status: 'processing' },
      CreateSaleMovementRequest & { __idemKey?: string }
    >({
      query: (values) => {
        const { __idemKey, ...requestValues } = values;
        return {
          url: `/api/salemovements`,
          method: 'POST',
          body: toSaleMovementApiRequest(requestValues),
          headers: __idemKey ? { 'Idempotency-Key': __idemKey } : undefined,
        };
      },
      transformResponse: (response: ISaleMovement | { status: 'processing' }) =>
        'status' in response && response.status === 'processing'
          ? response
          : normalizeSaleMovement(response as LegacySaleMovementResponse),
    }),
    getOperation: builder.query<SaleOperationStatus, string>({
      query: (key) => ({
        url: `/api/operations/${key}`,
        method: 'GET',
      }),
      transformResponse: (response: SaleOperationStatus): SaleOperationStatus =>
        response.status === 'done'
          ? {
              ...response,
              response: normalizeSaleMovement(response.response as LegacySaleMovementResponse),
            }
          : response,
    }),
    getSaleById: builder.query<ISaleMovement, string>({
      query: (id) => ({
        url: `/api/salemovements/${id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
      transformResponse: (response: LegacySaleMovementResponse) => normalizeSaleMovement(response),
    }),
    saleList: builder.query<IPagedResult<ISaleMovementListItem>, string>({
      query: (params) => ({
        url: `/api/salemovements?${params}`,
        method: 'GET',
      }),
      transformResponse: normalizeSaleMovementPage,
    }),
    cancelSale: builder.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/api/salemovements/${id}/cancel`,
        method: 'PATCH',
        body: { reason },
      }),
    }),
    updateSale: builder.mutation<ISaleMovement, CreateSaleMovementRequest & { id: string }>({
      query: (values) => ({
        url: `/api/salemovements/${values.id}`,
        method: 'PUT',
        body: toSaleMovementApiRequest(values),
      }),
      transformResponse: (response: LegacySaleMovementResponse) => normalizeSaleMovement(response),
    }),
    salePaymentMethods: builder.query<PaymentMethodGroupDto[], string>({
      query: (params) => ({
        url: `/api/salemovements/payments/summary?${params}`,
        method: 'GET',
      }),
    }),
    getStatusPorductsDespatch: builder.query<boolean, string>({
      query: (params) => ({
        url: `/api/salemovements/products/despatch-available/${params}`,
        method: 'GET',
      }),
    }),
    saleDespatchProducts: builder.mutation<ISaleDespatchSelectableProduct[], string[]>({
      query: (saleMovementIds) => ({
        url: '/api/salemovements/products/despatch-selection',
        method: 'POST',
        body: { saleMovementIds },
      }),
    }),
    saleDespatchAdvice: builder.mutation<string, ISaleDespatch>({
      query: (body) => ({
        url: '/api/salemovements/despatchAdvice',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLazyProductListQuery,
  useAddSaleMutation,
  useLazySaleListQuery,
  useLazyGetSaleByIdQuery,
  useCancelSaleMutation,
  useUpdateSaleMutation,
  useSalePaymentMethodsQuery,
  useLazySalePaymentMethodsQuery,
  useGetOperationQuery,
  useLazyGetOperationQuery,
  useLazyGetStatusPorductsDespatchQuery,
  useSaleDespatchProductsMutation,
  useSaleDespatchAdviceMutation,
} = saleApi;
